import React, { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { FormField } from './FormField';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Plus, Edit, Trash2, Cpu } from 'lucide-react';
import { toast } from 'sonner';

const DEVICE_TYPES = ['lock', 'door', 'gate', 'garage', 'switch', 'sensor'];

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ slug: '', name: '', type: 'lock', integration_id: '', external_id: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, iRes] = await Promise.all([api.get('/devices'), api.get('/integrations')]);
      setDevices(dRes.data);
      setIntegrations(iRes.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const EMPTY = { slug: '', name: '', type: 'lock', integration_id: '', external_id: '', sort_order: 0 };
  const openCreate = () => { setEditing(null); setForm(EMPTY); setPanelOpen(true); };
  const openEdit = (d) => { setEditing(d.id); setForm({ ...EMPTY, ...d }); setPanelOpen(true); };

  const save = async () => {
    if (!form.name || !form.slug) { toast.error('Nome e slug obbligatori'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/devices/${editing}`, form);
        toast.success('Dispositivo aggiornato');
      } else {
        await api.post('/devices', form);
        toast.success('Dispositivo creato');
      }
      setPanelOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm('Eliminare questo dispositivo?')) return;
    await api.delete(`/devices/${id}`);
    load();
    toast.success('Dispositivo eliminato');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Dispositivi Smart</h2>
        </div>
        <button onClick={openCreate} className="btn-gold flex items-center gap-2" data-testid="new-device-btn">
          <Plus size={14} /> Nuovo Dispositivo
        </button>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div> : (
        <div className="card-dark rounded-lg overflow-hidden">
          {devices.length === 0 ? (
            <div className="p-16 text-center">
              <Cpu size={32} className="text-[var(--lux-border-3)] mx-auto mb-4" strokeWidth={1} />
              <p className="text-[var(--lux-text-muted-3)] text-sm">Nessun dispositivo. Aggiungine uno.</p>
            </div>
          ) : (
            <table className="w-full table-dark">
              <thead><tr>
                <th className="text-left">Nome</th>
                <th className="text-left hidden sm:table-cell">Tipo</th>
                <th className="text-left hidden md:table-cell">Integrazione</th>
                <th className="text-left hidden md:table-cell">ID Esterno</th>
                <th />
              </tr></thead>
              <tbody>
                {devices.map(d => (
                  <tr key={d.id}>
                    <td>
                      <p className="text-white">{d.name}</p>
                      <p className="text-[var(--lux-text-muted-3)] text-xs font-mono">{d.slug}</p>
                    </td>
                    <td className="hidden sm:table-cell">
                      <span className="text-[var(--lux-text-muted-2)] text-xs capitalize px-2 py-0.5 bg-[var(--lux-surface-3)] rounded">{d.type}</span>
                    </td>
                    <td className="hidden md:table-cell text-[var(--lux-text-sec)] text-xs">{d.integration_name || '—'}</td>
                    <td className="hidden md:table-cell text-[var(--lux-text-muted-2)] text-xs font-mono">{d.external_id || '—'}</td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(d)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-white"><Edit size={13} /></button>
                        <button onClick={() => remove(d.id)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-red-400"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl font-light text-white">{editing ? 'Modifica' : 'Nuovo'} Dispositivo</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Nome *" name="name" form={form} setForm={setForm} testId="device-field-name" />
              <FormField label="Slug *" name="slug" form={form} setForm={setForm} testId="device-field-slug" />
            </div>
            <FormField label="Tipo" name="type" options={DEVICE_TYPES.map(t => ({ value: t, label: t }))} form={form} setForm={setForm} />
            <div>
              <label className="label-muted block mb-1">Integrazione</label>
              <select className="input-dark" value={form.integration_id || ''} onChange={e => setForm(p => ({ ...p, integration_id: e.target.value }))} data-testid="device-integration-select">
                <option value="">— Nessuna integrazione —</option>
                {integrations.map(i => <option key={i.id} value={i.id}>{i.name} ({i.brand})</option>)}
              </select>
            </div>
            <FormField label="ID Dispositivo nell'integrazione" name="external_id" form={form} setForm={setForm} testId="device-field-external_id" />
            <FormField label="Ordine" name="sort_order" type="number" form={form} setForm={setForm} testId="device-field-sort_order" />
            <div className="flex gap-3 pt-4 border-t border-[#222]">
              <button onClick={save} disabled={saving} className="btn-gold flex-1 flex items-center justify-center gap-2">
                {saving && <Loader2 size={13} className="animate-spin" />}
                Salva
              </button>
              <button onClick={() => setPanelOpen(false)} className="btn-outline-gold flex-1">Annulla</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
